import { Bullet, Label, Scrollbar, Tooltip, array, p50, percent } from "@amcharts/amcharts5";
import { AxisRendererX, AxisRendererY, CategoryAxis, ColumnSeries, ValueAxis, XYChart } from "@amcharts/amcharts5/xy";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { createDimensionNames, createMetricsValues } from "@/helpers/dataMapping";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { chartFontSettings } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class StackedColumn100Chart extends BaseChart {
  xAxis!: CategoryAxis<AxisRendererX>;
  yAxis!: ValueAxis<AxisRendererY>;
  names = [] as string[];
  metrics = [] as string[];

  groups = {};
  initChart() {
    this.root.container.children.clear();

    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        panX: false,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
      })
    );

    (this.chart as XYChart).set(
      "scrollbarX",
      Scrollbar.new(this.root, {
        orientation: "horizontal",
      })
    );

    this.createAxes();

    this.metrics.forEach((el, index) => {
      const paletteLength = this.palette.length;
      const indexColor = index % paletteLength;
      this.createSeries(el, indexColor, el);
    });

    this.showLegend(this.metrics, (this.chart as XYChart).series.values as LegendData);
  }

  private getCategoryCellWidth(): number {
    const plotW = (this.chart as XYChart).plotContainer.getPrivate("width") ?? 0;
    const count = this.xAxis?.dataItems?.length ?? 0;
    return count > 0 ? plotW / count : plotW;
  }

  protected createAxes() {
    const xRenderer = AxisRendererX.new(this.root, { minGridDistance: 30 });

    xRenderer.labels.template.setAll({
      ...chartFontSettings.label,
      textAlign: "center",
      oversizedBehavior: "truncate",
      maxWidth: 200,
      tooltipText: "{category}",
    });

    this.xAxis = (this.chart as XYChart).xAxes.push(
      CategoryAxis.new(this.root, {
        categoryField: "name",
        renderer: xRenderer,
        tooltip: Tooltip.new(this.root, {}),
      })
    );

    if (this.settings.xAxisName) {
      this.xAxis.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisName,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    this.xAxis.data.setAll(this.data);

    const updateXAxisLabelsLayout = () => {
      const cell = this.getCategoryCellWidth();
      const padding = 16;
      const maxWidth = Math.max(40, Math.floor(cell - padding));

      xRenderer.labels.template.setAll({
        maxWidth,
        rotation: 0,
        centerX: p50,
        centerY: 0.5,
        textAlign: "center",
        oversizedBehavior: "wrap",
      });
    };

    (this.chart as XYChart).plotContainer.onPrivate("width", updateXAxisLabelsLayout);
    (this.chart as XYChart).plotContainer.onPrivate("height", updateXAxisLabelsLayout);
    this.xAxis.events.on("datavalidated", updateXAxisLabelsLayout);
    (this.chart as XYChart).get("scrollbarX")?.thumb?.events.on("positionchanged", updateXAxisLabelsLayout);

    updateXAxisLabelsLayout();

    const yRenderer = AxisRendererY.new(this.root, {});
    yRenderer.labels.template.setAll({ ...chartFontSettings.label });

    this.yAxis = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        min: 0,
        max: 100,
        numberFormat: "#'%'",
        strictMinMax: true,
        calculateTotals: true,
        renderer: yRenderer,
      })
    );

    if (this.settings.yAxisName) {
      this.yAxis.children.push(
        Label.new(this.root, {
          text: this.settings.yAxisName,
          ...chartFontSettings.axisTitle,
          x: percent(-30),
          rotation: -90,
          y: percent(50),
          centerX: p50,
          marginTop: axisTitle.marginTop,
        })
      );
    }
  }

  protected createSeries(name: string, index: number, fieldName: string) {
    const shouldShowMetricValue = this.module === PLAN_MODULE ? Boolean(this.settings.showDataLabels) : false;

    const series = (this.chart as XYChart).series.push(
      ColumnSeries.new(this.root, {
        name: name,
        stacked: true,
        xAxis: this.xAxis,
        yAxis: this.yAxis,
        valueYField: fieldName,
        valueYShow: "valueYTotalPercent",
        categoryXField: "name",
        minBulletDistance: 80,
        fill: this.getSeriesColor(index),
      })
    );

    this.createTooltip(series as TooltipSeries, (target) => {
      let param: Record<string, unknown>[] = [];
      const tooltipValues = target?.dataItem?.dataContext as Record<string, unknown>;
      const xValue = series.get("valueYField");
      if (tooltipValues && xValue) {
        param = [
          {
            key: this.settings.decompositionMetric,
            value: tooltipValues.name,
          },
          {
            key: this.settings.decompositionMetricSecondary,
            value: xValue,
          },
          { key: this.settings.displayMetric, value: tooltipValues[xValue] },
        ];
      }
      return param;
    });

    series.columns.template.setAll({
      tooltipText: "{valueYTotalPercent}",
      tooltipY: percent(10),
    });
    series.data.setAll(this.data);

    const formatterConfig = this.getDataLabelsFormatterConfig();
    const metricKey = this.settings.displayMetric;
    const metricFormatter = this.formatters?.[metricKey];
    const metricFormatConfig = this.getMetricFormatter(metricKey, formatterConfig);
    const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

    series.columns.template.onPrivate("height", (height, target) => {
      const rawThreshold = Number(this.settings?.dataLabelsHideThreshold);
      const hideThreshold = Number.isFinite(rawThreshold) && rawThreshold > 0 ? rawThreshold : 60;

      array.each(target?.dataItem?.bullets || [], (bullet) => {
        const sprite = bullet.get("sprite");
        if (!sprite) {
          return;
        }

        if (Number(height) > hideThreshold) {
          sprite.show();
        } else {
          sprite.hide();
        }
      });
    });

    series.bullets.push(() => {
      const label = Label.new(this.root, {
        text: "{valueYField}",
        ...chartFontSettings.label,
        fill: this.getFill(series),
        rotation: this.settings.dataLabelsFlipHorizontally ? 0 : -90,
        centerY: p50,
        centerX: p50,
        populateText: true,
        textAlign: "center",
        oversizedBehavior: "truncate",
        fontSize: labelFontSize,
      });

      label.adapters.add("text", (labelText, target) => {
        const baseText = labelText ?? "";
        const rawValue = Number(target?.dataItem?.get("valueY" as never));

        if (!Number.isFinite(rawValue) || rawValue === 0) {
          return baseText;
        }

        if (!shouldShowMetricValue) {
          return baseText;
        }

        const formattedValue = metricFormatter
          ? metricFormatter(rawValue, formatterConfig)
          : this.root.numberFormatter.format(rawValue, metricFormatConfig);

        if (!formattedValue || formattedValue === baseText) {
          return baseText;
        }

        return `${baseText}\n${formattedValue}`;
      });

      label.adapters.add("visible", (visible) => {
        return visible;
      });

      return Bullet.new(this.root, {
        sprite: label,
        locationY: 0.5,
      });
    });

    return series;
  }

  protected mapData(data: []): ChartDataRecord[] {
    let mappedData = [] as ChartDataRecord[];

    this.names = [];
    this.metrics = [];

    const firstDimension = this.settings.decompositionMetric;
    const secondDimension = this.settings.decompositionMetricSecondary;
    const displayMetric = this.settings.displayMetric;

    createDimensionNames(data, firstDimension, this.names);
    createDimensionNames(data, secondDimension, this.metrics);

    createMetricsValues(mappedData, data, firstDimension, secondDimension, displayMetric, this.names);

    const findTotal = (param: Record<string, unknown>) =>
      Object.values(param).reduce((acc: number, curr) => (typeof curr === "number" ? acc + curr : acc), 0);

    const skipSort = Boolean(this.settings?.keepApiOrder);
    if (!skipSort) {
      mappedData.sort((a, b) => findTotal(b) - findTotal(a));
    }
    mappedData = this.showItems(mappedData as ChartDataRecord[], "name", undefined);
    mappedData = mappedData.filter((item) => findTotal(item) !== 0 && !!item.name);

    return mappedData;
  }
}
